/**
 * Provider Actions API (New Backend)
 *
 * REST endpoints for provider CRUD operations.
 * Backend: accounts/views.py
 *
 * Endpoints:
 *   POST   /api/providers/create/                  → Create provider
 *   PUT    /api/providers/<uuid>/update/            → Full update
 *   PATCH  /api/providers/<uuid>/update/            → Partial update
 *   DELETE /api/providers/<uuid>/delete/            → Soft-delete
 */

import apiClient from '@/api/client'
import type {
  CreateProviderInput,
  CreateProviderResponse,
  UpdateProviderInput,
  UpdateProviderResponse,
  DeleteProviderResponse,
} from '@/types/provider'

// ============================================================================
// Create
// ============================================================================

/**
 * Create a new provider profile with user account.
 * The backend creates: User → Profile → ProfileProvider.
 */
export async function createProvider(
  input: CreateProviderInput
): Promise<CreateProviderResponse> {
  const resp = await apiClient.post<CreateProviderResponse>(
    '/api/providers/create/',
    input
  )
  return resp.data
}

// ============================================================================
// Update
// ============================================================================

/**
 * Update an existing provider (partial update via PATCH).
 */
export async function updateProvider(
  providerId: string,
  input: UpdateProviderInput
): Promise<UpdateProviderResponse> {
  const resp = await apiClient.patch<UpdateProviderResponse>(
    `/api/providers/${providerId}/update/`,
    input
  )
  return resp.data
}

/**
 * Full update of a provider (PUT).
 */
export async function replaceProvider(
  providerId: string,
  input: UpdateProviderInput
): Promise<UpdateProviderResponse> {
  const resp = await apiClient.put<UpdateProviderResponse>(
    `/api/providers/${providerId}/update/`,
    input
  )
  return resp.data
}

// ============================================================================
// Delete
// ============================================================================

/**
 * Soft-delete a provider profile.
 */
export async function deleteProvider(
  providerId: string
): Promise<DeleteProviderResponse> {
  const resp = await apiClient.delete<DeleteProviderResponse>(
    `/api/providers/${providerId}/delete/`
  )
  return resp.data
}
